import { useContext } from "react";
import {
  AddAttachmentsResponse,
  AddCommentResponse,
  AllCoreReturnsRequestV2,
  AllCoreReturnsResponse,
  CancelReturnResponse,
  ConfirmShipmentRequestBody,
  CoreReturnDetails,
  CoreReturnsPayload,
  CreateReturn,
  CreateReturnResponse,
  DealerPreparationSaveRequest,
  DownloadAttachmentResponse,
  DownloadCoreAttachmentResponse,
  DownloadCoreAuthFormResponse,
  EligibilitiesRequestV2,
  EligibilitiesResponse,
  InspectionResultsResponse,
  LineItem,
  ReturnAddresses,
  ReturnDetailsResponse,
  ReturnReasonResponse,
  ReturnsApiV2,
  ReturnsRequestV2WithoutUrlAndToken,
  ReturnsRequestWithoutUrlAndToken,
  ReturnsResponse,
  SurplusCsooOptions,
  SurplusReturnDetailsResponse,
  SurplusReturnsRequest,
  UpdateSurplusReturnCsooPayload,
  UpdateSurplusReturnPayload,
  ValidateEligibilitiesRequestV2,
} from "./returnsInterfaces";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";

const useReturnsApi = (): ReturnsApiV2 => {
  const axios = useContext(ApiContext);
  const { RETURNS_SERVICE_URL: baseUrl } = useContext(ConfigContext);
  const apiUrl = `${baseUrl}/api/v1/returns`;
  const apiV2Url = `${baseUrl}/api/v2/returns`;

  return {
    getAllReturns: async ({
      accountId,
      ...params
    }: ReturnsRequestWithoutUrlAndToken): Promise<ReturnsResponse> => {
      const response = await axios.getWithRetries(apiUrl, {
        headers: { "x-account-id": accountId },
        params,
      });
      return response.data;
    },

    getAllReturnsV2: async (
      payload: ReturnsRequestV2WithoutUrlAndToken
    ): Promise<ReturnsResponse> => {
      const response = await axios.postWithRetries(
        `${apiV2Url}/search`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },

    cancelSurplusReturn: async (
      returnId: string,
      accountId?: string
    ): Promise<CancelReturnResponse> => {
      const response = await axios.putWithRetries(`${apiUrl}/surplus`, null, {
        headers: {
          ...(accountId && { "x-account-id": accountId }),
        },
        params: {
          returnId,
        },
      });
      return response.data;
    },

    getSurplusReturnDetails: async (
      returnId: string,
      accountId: string,
      lang: string
    ): Promise<SurplusReturnDetailsResponse> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/surplus/${returnId}`,
        {
          headers: {
            "x-account-id": accountId,
          },
          params: {
            lang,
          },
        }
      );
      return response.data;
    },

    getSurplusReturnCsooOptions: async (
      returnId: string,
      accountId: string,
      lang: string
    ): Promise<SurplusCsooOptions[]> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/surplus/${returnId}/csoo-options`,
        {
          headers: {
            "x-account-id": accountId,
          },
          params: {
            lang,
          },
        }
      );
      return response.data;
    },

    reviewSurplusLineItems: async (
      accountId: string,
      language: string = "en_US",
      lineItems: LineItem[]
    ) => {
      const response = await axios.postWithRetries(
        `${apiUrl}/surplus/review`,
        lineItems,
        {
          headers: {
            "Content-Type": "application/json",
            "x-account-id": accountId,
          },
          params: language ? { lang: language } : undefined,
        }
      );
      return response.data;
    },

    reviewDailyReturnsLineItems: async (
      accountId: string,
      invoiceNumber: string,
      language: string = "en_US",
      lineItems: {
        invoiceNumber: string;
        invoiceLineNumber: string;
        partNumber: string;
        returnQty: number;
        returnReason: string;
      }[]
    ) => {
      const response = await axios.postWithRetries(
        `${apiUrl}/review`,
        {
          dealerId: accountId,
          invoiceNumber,
          lineItems,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-account-id": accountId,
          },
          params: language ? { lang: language } : undefined,
        }
      );
      return response.data.lineItems;
    },

    updateSurplusReturn: async (
      updateRequest: UpdateSurplusReturnPayload
    ): Promise<CreateReturnResponse> => {
      const jsonBlob = new Blob([JSON.stringify(updateRequest)], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);

      const response = await axios.patchWithRetries(`${apiUrl}/surplus`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(updateRequest.accountId && {
            "x-account-id": updateRequest.accountId,
          }),
        },
      });
      return response.data;
    },

    updateSurplusReturnCsoo: async (
      updateRequest: UpdateSurplusReturnCsooPayload,
      accountId: string
    ): Promise<CreateReturnResponse> => {
      const response = await axios.postWithRetries(
        `${apiUrl}/surplus/save-csoo`,
        updateRequest,
        {
          headers: {
            "Content-Type": "application/json",
            "x-account-id": accountId,
          },
        }
      );
      return response.data;
    },

    createSurplusReturn: async (
      submitRequest: SurplusReturnsRequest,
      attachments: File[]
    ) => {
      const jsonBlob = new Blob([JSON.stringify(submitRequest)], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(`${apiUrl}/surplus`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },

    getSurplusReturnAddresses: async (
      dealerId: string,
      returnId?: string
    ): Promise<ReturnAddresses> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/surplus/addresses`,
        {
          params: {
            dealerId,
            returnId,
          },
        }
      );
      return response.data;
    },

    addCommentForSurplusReturn: async (
      returnId: string,
      comment: string,
      accountId?: string
    ): Promise<AddCommentResponse> => {
      const data = { returnId, comment };
      const response = await axios.postWithRetries(
        `${apiUrl}/surplus/comments`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            ...(accountId && { "x-account-id": accountId }),
          },
        }
      );

      return { ...response.data, status: response.status };
    },

    addAttachmentsForSurplusReturn: async (
      returnId: string,
      attachments: File[],
      accountId?: string
    ): Promise<AddAttachmentsResponse> => {
      const jsonBlob = new Blob([JSON.stringify({ returnId })], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(
        `${apiUrl}/surplus/attachments`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(accountId && { "x-account-id": accountId }),
          },
        }
      );

      return response.data;
    },

    downloadAttachmentForSurplusReturn: async (
      returnId: string,
      phioObjid: string
    ): Promise<DownloadAttachmentResponse> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/surplus/attachment`,
        {
          params: {
            returnId,
            phioObjid,
          },
        }
      );

      return { ...response.data, status: response.status };
    },

    createDailyReturn: async (
      createReturn: CreateReturn,
      language: string,
      attachments: File[]
    ): Promise<CreateReturnResponse> => {
      const jsonBlob = new Blob([JSON.stringify(createReturn)], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(apiUrl, data, {
        headers: { "Content-Type": "multipart/form-data" },
        params: language ? { language } : undefined,
      });

      return response.data;
    },

    getDailyReturnDetails: async (
      returnId: string,
      accountId: string,
      lang: string,
      withLeadingZeros: boolean
    ): Promise<ReturnDetailsResponse> => {
      const response = await axios.getWithRetries(`${apiUrl}/${returnId}`, {
        headers: { "x-account-id": accountId },
        params: { lang, withLeadingZeros },
      });
      return response.data;
    },

    getReturnReasonDetails: async (
      accountId: string,
      language: string
    ): Promise<ReturnReasonResponse> => {
      const response = await axios.getWithRetries(`${apiUrl}/reasons`, {
        headers: { "account-id": accountId },
        params: language ? { language } : undefined,
      });
      return response.data;
    },

    getDailyReturnAddresses: async (
      invoiceNumber: string
    ): Promise<ReturnAddresses> => {
      const response = await axios.getWithRetries(`${apiUrl}/addresses`, {
        params: { invoiceNumber },
      });
      return response.data;
    },

    cancelDailyReturn: async (returnId): Promise<CancelReturnResponse> => {
      const response = await axios.putWithRetries(apiUrl, null, {
        params: { returnId },
      });
      return response.data;
    },

    addCommentToDailyReturn: async (
      returnId,
      comment,
      accountId
    ): Promise<AddCommentResponse> => {
      const jsonBlob = new Blob([JSON.stringify({ returnId, comment })], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);

      const response = await axios.postWithRetries(
        `${apiUrl}/addComment`,
        data,
        {
          headers: {
            "Content-Type": "string",
            "x-account-id": accountId ?? "",
          },
        }
      );

      return { ...response.data, status: response.status };
    },

    addAttachmentsToDailyReturn: async (
      returnId,
      attachments,
      accountId,
      nfeReferenceDate,
      nfeReferenceNumber
    ): Promise<AddAttachmentsResponse> => {
      const jsonBlob = new Blob(
        [JSON.stringify({ returnId, nfeReferenceDate, nfeReferenceNumber })],
        {
          type: "application/json",
        }
      );
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(
        `${apiUrl}/addAttachments`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-account-id": accountId ?? "",
          },
        }
      );

      return response.data;
    },

    downloadAttachmentFromDailyReturn: async (
      returnId,
      phioObjId
    ): Promise<DownloadAttachmentResponse> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/downloadAttachment`,
        {
          params: {
            returnId,
            phioObjid: phioObjId,
          },
        }
      );
      return { ...response.data, status: response.status };
    },

    getAllCoreReturns: async (
      allCoreReturnsRequest: AllCoreReturnsRequestV2
    ): Promise<AllCoreReturnsResponse> => {
      const { accountId, ...params } = allCoreReturnsRequest;

      const response = await axios.getWithRetries(
        `${apiUrl}/core/allCoreReturns`,
        {
          headers: { "account-id": accountId },
          params,
        }
      );

      return response.data;
    },

    getEligibilities: async ({
      accountId,
      ...params
    }: EligibilitiesRequestV2): Promise<EligibilitiesResponse> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/core/eligibilities`,
        {
          headers: { "account-id": accountId },
          params,
        }
      );
      return response.data;
    },

    getEligibilitiesAsExcel: async ({
      accountId,
      ...params
    }: EligibilitiesRequestV2): Promise<any> =>
      axios.getWithRetries(`${apiUrl}/core/eligibilities`, {
        responseType: "blob",
        headers: {
          "account-id": accountId,
          Accept: "application/vnd.ms-excel",
        },
        params,
      }),

    validateCorePartsEligibility: async ({
      accountId,
      coreParts,
    }: ValidateEligibilitiesRequestV2): Promise<EligibilitiesResponse> => {
      const jsonBlob = new Blob([JSON.stringify({ accountId, coreParts })], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("validateCorePartsRequest", jsonBlob);
      const response = await axios.postWithRetries(
        `${apiUrl}/core/eligibilities/validate`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    },

    getCoreReturnDetails: async (
      returnId: string,
      accountId: string,
      lang: string
    ): Promise<CoreReturnDetails> => {
      const response = await axios.getWithRetries(
        `${apiUrl}/core/${returnId}`,
        {
          params: { lang, dealerId: accountId },
        }
      );
      return mapToCoreReturnDetails(response.data);
    },

    cancelCoreReturn: async (
      coreReturnId: string,
      dealerId: string
    ): Promise<CancelReturnResponse> => {
      const response = await axios.putWithRetries(`${apiUrl}/core`, null, {
        params: { coreReturnId, dealerId },
      });
      return response.data;
    },

    createCoreReturn: async (
      coreReturnsPayload: CoreReturnsPayload,
      attachments: File[]
    ): Promise<CreateReturnResponse> => {
      const jsonBlob = new Blob([JSON.stringify(coreReturnsPayload)], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(`${apiUrl}/core`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },

    addCommentForCoreReturn: async (
      returnId: string,
      comment: string
    ): Promise<AddCommentResponse> => {
      const data = { returnId, comment };
      const response = await axios.postWithRetries(
        `${apiUrl}/core/comments`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return { ...response.data, status: response.status };
    },

    addAttachmentsForCoreReturn: async (
      returnId: string,
      attachments: File[]
    ): Promise<AddAttachmentsResponse> => {
      const jsonBlob = new Blob([JSON.stringify({ returnId })], {
        type: "application/json",
      });
      const data = new FormData();
      data.append("body", jsonBlob);
      if (attachments) {
        attachments.forEach((file) => {
          data.append("files", file);
        });
      }

      const response = await axios.postWithRetries(
        `${apiUrl}/core/attachments`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: {
            returnId,
          },
        }
      );

      return response.data;
    },

    downloadAttachmentForCoreReturn: async (
      returnId: string,
      phioObjid: string
    ): Promise<DownloadCoreAttachmentResponse> =>
      axios.getWithRetries(`${apiUrl}/core/downloadAttachment`, {
        params: {
          returnId,
          phioObjid,
        },
        responseType: "blob",
      }),

    downloadCoreAuthFormAttachment: async (
      returnId: string,
      shipToId: string,
      lang: string
    ): Promise<DownloadCoreAuthFormResponse> =>
      axios.getWithRetries(`${apiUrl}/core/coreAuthForm`, {
        params: {
          returnId,
          shipToId,
          lang,
        },
        responseType: "blob",
      }),

    getDailyReturnInspectionResults: async (
      returnId: string,
      accountId: string,
      lang: string,
      withLeadingZeros: boolean
    ): Promise<InspectionResultsResponse> => {
      const url = `${apiUrl}/${returnId}/inspection-results`;

      return axios.getWithRetries(url, {
        params: {
          lang,
          withLeadingZeros,
        },
        headers: {
          "x-account-id": accountId,
        },
      });
    },

    saveDealerPreparation: async (
      returnId: string,
      accountId: string,
      request: DealerPreparationSaveRequest
    ): Promise<void> => {
      await axios.patchWithRetries(
        `${apiUrl}/${returnId}/save-dealer-preparation`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
            "x-account-id": accountId,
          },
        }
      );
    },

    confirmShipment: async (
      accountId: string,
      request: ConfirmShipmentRequestBody
    ): Promise<void> => {
      await axios.postWithRetries(`${apiUrl}/confirmShipment`, request, {
        headers: {
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
      });
    },
  };
};

const mapToCoreReturnDetails = (response: any): CoreReturnDetails => ({
  coreReturnId: response.coreReturnId,
  returnType: response.returnType,
  dealerReference: response.dealerReference,
  currency: response.currency,
  returnDate: response.returnDate,
  headerStatus: response.headerStatus,
  headerStatusDescription: response.headerStatusDescription,
  shipFromAddress: {
    name: response.shipFromName,
    street: response.shipFromStreet,
    city: response.shipFromCity,
    postCode: response.shipFromPostCode,
    region: response.shipFromRegion,
    country: response.shipFromCountry,
  },
  coreReturnItems: response.coreReturnItems.map((item: any) => ({
    partNumber: item.partNumber,
    partDescription: item.partDescription,
    purchasedSerialNumber: item.purchasedSerialNumber,
    returnedSerialNumber: item.returnedSerialNumber,
    warrantyClaimNumber: item.warrantyClaimNumber,
    billingNumber: item.billingNumber,
    billingDate: item.billingDate,
    corePrice: item.corePrice,
    currency: item.currency,
    itemStatus: item.itemStatus,
    itemStatusDescription: item.itemStatusDescription,
    shipToAddress: {
      name: item.shipToName,
      street: item.shipToStreet,
      city: item.shipToCity,
      postCode: item.shipToPostCode,
      region: item.shipToRegion,
      country: item.shipToCountry,
    },
  })),
  coreReturnComments: response.coreReturnComments,
  coreReturnAttachments: response.coreReturnAttachments,
});

export default useReturnsApi;
